import { action, computed, observable } from 'mobx';
import { MapView, SceneView } from 'cd-eft/utils/esriTypes';
import Graphic from '@arcgis/core/Graphic';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';

// a domain value enumeration to help ensure that consistent string values
// are supplied to the construtor and viewStyle property of the DualEsriViewModel class
export enum ViewStyleEnum {
  corner = 'corner',
  horizontal = 'horizontal',
  vertical = 'vertical',
}

// interface declaring the expected constructor parameters
export interface IDualEsriViewModelConstructorProps {
  viewStyle: ViewStyleEnum;
  viewA: MapView | SceneView;
  viewB: MapView | SceneView;
  mouseTracking: boolean;
  viewpointTracking: boolean;
  viewpointSynching: boolean;
}

// generic interface that exposes the dispose() method
interface IDisposable {
  dispose(): void;
  isDisposed(): boolean;
}

// main class that controls DualEsriView componenent
export class DualEsriViewModel implements IDisposable {
  // public observable properties that are referenced in the DualEsriView.tsx file
  @observable viewStyle: ViewStyleEnum;
  @observable switchPrimaryView: boolean;

  // private observable properties that support public computed properties in this class
  @observable private __mouseTracking: boolean = false;
  @observable private __viewpointTracking: boolean = false;
  @observable private __viewpointSynching: boolean = false;
  @observable private __viewA: MapView | SceneView; // do not destroy
  @observable private __viewB: MapView | SceneView; // do not destroy

  // private properties defined by this object, to be cleaned up at dispose()
  private __activeView: MapView | SceneView | undefined; // do not destroy()
  private __mpGraphicA1: Graphic;
  private __mpGraphicA2: Graphic;
  private __mpGraphicB1: Graphic;
  private __mpGraphicB2: Graphic;
  private __vpGraphicA1: Graphic;
  private __vpGraphicA2: Graphic;
  private __vpGraphicB1: Graphic;
  private __vpGraphicB2: Graphic;

  // event/watch handlers to be cleaned up at dispose()
  private __interactionWatch1: __esri.WatchHandle;
  private __interactionWatch2: __esri.WatchHandle;
  private __viewpointWatch1: __esri.WatchHandle;
  private __viewpointWatch2: __esri.WatchHandle;
  private __pointerMoveEvent1: IHandle;
  private __pointerMoveEvent2: IHandle;
  private __mapDragEvent: IHandle;

  constructor(props: IDualEsriViewModelConstructorProps) {
    // use supplied constructor props
    this.__viewA = props.viewA;
    this.__viewB = props.viewB;
    this.viewStyle = props.viewStyle;
    this.__mouseTracking = props.mouseTracking;
    this.__viewpointTracking = props.viewpointTracking;
    this.__viewpointSynching = props.viewpointSynching;

    // initialize other props
    this.switchPrimaryView = false;

    // initialize graphics
    this.__mpGraphicA1 = new Graphic({
      symbol: ____mouseSymbol1,
    });
    this.__mpGraphicA2 = new Graphic({
      symbol: ____mouseSymbol2,
    });
    this.__mpGraphicB1 = new Graphic({
      symbol: ____mouseSymbol1,
    });
    this.__mpGraphicB2 = new Graphic({
      symbol: ____mouseSymbol2,
    });
    this.__vpGraphicA1 = new Graphic({
      symbol: ____viewpointSymbol1,
    });
    this.__vpGraphicA2 = new Graphic({
      symbol: ____viewpointSymbol2,
    });
    this.__vpGraphicB1 = new Graphic({
      symbol: ____viewpointSymbol1,
    });
    this.__vpGraphicB2 = new Graphic({
      symbol: ____viewpointSymbol2,
    });

    // load graphics into corresponding view.graphics collections
    this.__viewA.graphics.add(this.__mpGraphicA1);
    this.__viewA.graphics.add(this.__mpGraphicA2);
    this.__viewA.graphics.add(this.__vpGraphicA1);
    this.__viewA.graphics.add(this.__vpGraphicA2);
    this.__viewB.graphics.add(this.__mpGraphicB1);
    this.__viewB.graphics.add(this.__mpGraphicB2);
    this.__viewB.graphics.add(this.__vpGraphicB1);
    this.__viewB.graphics.add(this.__vpGraphicB2);

    // setup watch handlers for view.interacting and view.animation properties
    this.__interactionWatch1 = this.__viewA.watch(
      ['interacting', 'animation'],
      this.__interactionWatchHandler.bind(this)
    );
    this.__interactionWatch2 = this.__viewB.watch(
      ['interacting', 'animation'],
      this.__interactionWatchHandler.bind(this)
    );

    // setup watch handlers for view.viewpoint property
    this.__viewpointWatch1 = this.__viewA.watch('viewpoint', this.__viewpointWatchHandler.bind(this));
    this.__viewpointWatch2 = this.__viewB.watch('viewpoint', this.__viewpointWatchHandler.bind(this));

    // setup event handlers for view pointerMove events
    this.__pointerMoveEvent1 = this.__viewA.on(
      'pointer-move',
      // NOTE: using alternative object binding to enable handler function reuse
      this.__pointerMoveEventHandler.bind({ this: this, eventSource: this.__viewA })
    );
    this.__pointerMoveEvent2 = this.__viewB.on(
      'pointer-move',
      // NOTE: using alternative object binding to enable handler function reuse
      this.__pointerMoveEventHandler.bind({ this: this, eventSource: this.__viewB })
    );

    // initial disabling the left button drag on the secondary view
    this.__mapDragEvent = this.__viewB.on('drag', this.__mapDragEventHandler);
  }

  private __isDisposed = false;
  isDisposed(): boolean {
    return this.__isDisposed;
  }

  @action dispose(): void {
    if (this.isDisposed()) return;

    // a disposal method that tells the object to dispose of, or release any resources it is referencing
    // to ensure that the object no longer interferes with any aspect of the application
    // must be invoked in code by the developer, e.g. someObject.dispose(), once called, it cannot be undone

    // disable options that switch on/off event handlers emitted by external sources
    this.__mouseTracking = false;
    this.__viewpointTracking = false;
    this.__viewpointSynching = false;

    // detach event/watch handler references, which in turn may allow the referenced objects to get garbage collected
    this.__interactionWatch1.remove();
    this.__interactionWatch2.remove();
    this.__viewpointWatch1.remove();
    this.__viewpointWatch2.remove();
    this.__pointerMoveEvent1.remove();
    this.__pointerMoveEvent2.remove();

    this.__interactionWatch1 = undefined as any;
    this.__interactionWatch2 = undefined as any;
    this.__viewpointWatch1 = undefined as any;
    this.__viewpointWatch2 = undefined as any;
    this.__pointerMoveEvent1 = undefined as any;
    this.__pointerMoveEvent2 = undefined as any;

    // remove the graphic objects created by this class from the corresponding esriView objects' graphics collections
    this.__viewA.graphics.remove(this.__mpGraphicA1);
    this.__viewA.graphics.remove(this.__mpGraphicA2);
    this.__viewB.graphics.remove(this.__mpGraphicB1);
    this.__viewB.graphics.remove(this.__mpGraphicB2);
    this.__viewA.graphics.remove(this.__vpGraphicA1);
    this.__viewA.graphics.remove(this.__vpGraphicA2);
    this.__viewB.graphics.remove(this.__vpGraphicB1);
    this.__viewB.graphics.remove(this.__vpGraphicB2);

    // destory graphics created by this object instance
    this.__mpGraphicA1.destroy();
    this.__mpGraphicA2.destroy();
    this.__mpGraphicB1.destroy();
    this.__mpGraphicB2.destroy();
    this.__vpGraphicA1.destroy();
    this.__vpGraphicA2.destroy();
    this.__vpGraphicB1.destroy();
    this.__vpGraphicB2.destroy();

    // set references to null to allow for garbage collection
    this.__mpGraphicA1 = undefined as any;
    this.__mpGraphicA2 = undefined as any;
    this.__mpGraphicB1 = undefined as any;
    this.__mpGraphicB2 = undefined as any;
    this.__vpGraphicA1 = undefined as any;
    this.__vpGraphicA2 = undefined as any;
    this.__vpGraphicB1 = undefined as any;
    this.__vpGraphicB2 = undefined as any;

    // release references to esriView obejcts to allow for garbage collection if/when they are no longer needed by the application
    this.__viewA = undefined as any;
    this.__viewB = undefined as any;
    this.__activeView = undefined as any;

    this.__isDisposed = true;
  }

  private __interactionWatchHandler(newValue, oldValue, propertyName, target) {
    // exit private method if disposed
    if (this.isDisposed()) {
      // console.warn('Invalid operation: the object has been disposed.');
      return;
    }

    // update the activeView reference
    if (this.__viewA.interacting || this.__viewA.animation) {
      this.__activeView = this.__viewA;
    }
    if (this.__viewB.interacting || this.__viewB.animation) {
      this.__activeView = this.__viewB;
    }

    // update the viewpoint of the opposing view
    if (this.viewpointSynching && this.__activeView?.viewpoint && this.__activeView === this.__viewA) {
      this.__viewB.viewpoint = this.__activeView.viewpoint.clone();
    }
    if (this.viewpointSynching && this.__activeView?.viewpoint && this.__activeView === this.__viewB) {
      this.__viewA.viewpoint = this.__activeView.viewpoint.clone();
    }
  }

  private __viewpointWatchHandler(newValue, oldValue, propertyName, target) {
    // exit private method if disposed
    if (this.isDisposed()) {
      return;
    }

    // update the current viewpoint of the opposing view
    if (this.viewpointSynching) {
      if (newValue && target === this.__activeView) {
        if (target === this.__viewA) {
          this.__viewB.viewpoint = newValue.clone();
        }
        if (target === this.__viewB) {
          this.__viewA.viewpoint = newValue.clone();
        }
      }
    }

    // update the viewpoint locator graphic of the opposing view
    if (this.viewpointTracking && target === this.__viewA) {
      if (newValue) {
        this.__vpGraphicB1.geometry = newValue.targetGeometry;
        this.__vpGraphicB2.geometry = newValue.targetGeometry;
      } else {
        this.__vpGraphicB1.geometry = undefined as any;
        this.__vpGraphicB2.geometry = undefined as any;
      }
    }
    // update the viewpoint locator graphic of the opposing view
    if (this.viewpointTracking && target === this.__viewB) {
      if (newValue) {
        this.__vpGraphicA1.geometry = newValue.targetGeometry;
        this.__vpGraphicA2.geometry = newValue.targetGeometry;
      } else {
        this.__vpGraphicA1.geometry = undefined as any;
        this.__vpGraphicA2.geometry = undefined as any;
      }
    }
  }

  // NOTE: this method expects the binding context of 'this' to be an alternate object
  // that includes reference to the expected 'this', as well as the source object that emits the event,
  // because the event data does not include information about its source
  // see wireup implementation in constructor()
  private __pointerMoveEventHandler(e) {
    // read context method is bound to
    const bindings = (this as unknown) as { this: DualEsriViewModel; eventSource: MapView | SceneView };

    // exit private method if disposed
    if (bindings.this.isDisposed()) {
      // console.warn('Invalid operation: the object has been disposed.');
      return;
    }

    // update the mouse cursor location graphics
    if (bindings.this.mouseTracking) {
      const point = bindings.eventSource.toMap(e);
      if (point) {
        bindings.this.__mpGraphicA1.geometry = point;
        bindings.this.__mpGraphicA2.geometry = point;
        bindings.this.__mpGraphicB1.geometry = point;
        bindings.this.__mpGraphicB2.geometry = point;
      }
    }
  }

  @computed get viewpointSynching(): boolean {
    return this.__viewpointSynching;
  }
  set viewpointSynching(value: boolean) {
    this.__viewpointSynching = value;

    if (value) {
      // update the current viewpoint of the opposing view
      // use the current activeView, or default to primaryView, as viewpoint source
      const source = this.__activeView || this.primaryView;
      if (source === this.__viewA) {
        this.__viewB.viewpoint = source.viewpoint.clone();
      }
      if (source === this.__viewB) {
        this.__viewA.viewpoint = source.viewpoint.clone();
      }
    }
  }

  @computed get mouseTracking(): boolean {
    return this.__mouseTracking;
  }
  set mouseTracking(value: boolean) {
    // update the private property
    this.__mouseTracking = value;

    if (!value) {
      // remove any existing geometry to clear the graphic
      this.__mpGraphicA1.geometry = undefined as any;
      this.__mpGraphicA2.geometry = undefined as any;
      this.__mpGraphicB1.geometry = undefined as any;
      this.__mpGraphicB2.geometry = undefined as any;
    }
  }

  @computed get viewpointTracking(): boolean {
    return this.__viewpointTracking;
  }
  set viewpointTracking(value: boolean) {
    // update the private property
    this.__viewpointTracking = value;

    if (!value) {
      // remove any existing geometry to clear the graphic
      this.__vpGraphicA1.geometry = undefined as any;
      this.__vpGraphicA2.geometry = undefined as any;
      this.__vpGraphicB1.geometry = undefined as any;
      this.__vpGraphicB2.geometry = undefined as any;
    } else {
      // apply the current view location geometry to display the graphic
      if (this.__viewB && this.__viewB.viewpoint) this.__vpGraphicA1.geometry = this.__viewB.viewpoint.targetGeometry;
      if (this.__viewB && this.__viewB.viewpoint) this.__vpGraphicA2.geometry = this.__viewB.viewpoint.targetGeometry;
      if (this.__viewA && this.__viewA.viewpoint) this.__vpGraphicB1.geometry = this.__viewA.viewpoint.targetGeometry;
      if (this.__viewA && this.__viewA.viewpoint) this.__vpGraphicB2.geometry = this.__viewA.viewpoint.targetGeometry;
    }
  }

  @computed get primaryView(): MapView | SceneView {
    // return either view A or B as primary, depending on the switchPrimaryView value
    if (!this.switchPrimaryView) {
      return this.__viewA;
    } else {
      return this.__viewB;
    }
  }

  @computed get secondaryView(): MapView | SceneView {
    // return either view A or B secondary, depending on the switchPrimaryView value
    if (!this.switchPrimaryView) {
      if (this.__mapDragEvent) this.__mapDragEvent.remove();
      this.__mapDragEvent = this.__viewB.on('drag', this.__mapDragEventHandler);

      return this.__viewB;
    } else {
      if (this.__mapDragEvent) this.__mapDragEvent.remove();
      this.__mapDragEvent = this.__viewA.on('drag', this.__mapDragEventHandler);

      return this.__viewA;
    }
  }

  private __mapDragEventHandler(event) {
    if (event.button === 0) {
      event.stopPropagation();
    }
  }
}

export default DualEsriViewModel;

// define the map symbols used in this class module
const ____mouseSymbol1 = new SimpleMarkerSymbol({
  style: 'cross',
  size: 12,
  xoffset: -1,
  yoffset: -1,
  color: 'transparent',
  outline: {
    color: [96, 96, 96, 1],
    width: '1px',
  },
});
const ____mouseSymbol2 = new SimpleMarkerSymbol({
  style: 'cross',
  size: 12,
  xoffset: 0,
  yoffset: 0,
  color: 'transparent',
  outline: {
    color: [239, 239, 239, 1],
    width: '1px',
  },
});
const ____viewpointSymbol1 = new SimpleMarkerSymbol({
  style: 'square',
  size: 10,
  color: 'transparent',
  outline: {
    color: [96, 96, 96, 1],
    width: '1px',
  },
});
const ____viewpointSymbol2 = new SimpleMarkerSymbol({
  style: 'square',
  size: 8,
  color: 'transparent',
  outline: {
    color: [223, 223, 223, 1],
    width: '1px',
  },
});
